import { Info, CheckCircle2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Shared helper
// ---------------------------------------------------------------------------

interface InfoBlockProps {
  label: string;
  title: React.ReactNode;
  body: React.ReactNode;
}

/**
 * Renders a labelled info section used on the education pages.
 * Label is shown in small caps above the title and body copy.
 */
function InfoBlock({ label, title, body }: InfoBlockProps) {
  return (
    <div className="flex flex-col gap-2.5 w-full">
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
        {label}
      </p>
      <div className="flex flex-col gap-2.5 w-full">
        <p className="font-medium text-zinc-800 text-xl leading-7">{title}</p>
        <div className="text-sm text-zinc-500 leading-normal">{body}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page content components (read-only, no interactive state)
// ---------------------------------------------------------------------------

/** Page 1: Setup disaster recovery intro */
export function IntroContent() {
  return (
    <InfoBlock
      label="Onboarding"
      title="This onboarding will teach you about disaster recovery and walk you through setting it up step-by-step."
      body="You'll learn what disaster recovery is, how it works, and the risks involved before setting it up."
    />
  );
}

/** Page 2: What is disaster recovery? */
export function WhatIsDRContent() {
  return (
    <InfoBlock
      label="Recovering funds"
      title={`Disaster recovery is a "break glass in case of emergency" feature that ensures your organization's funds are always accessible.`}
      body="Disaster recovery lets you withdraw funds and continue operating your MLS accounts even in the unlikely event that the MLS Guardian or Den are not operating."
    />
  );
}

/** Page 3: How does disaster recovery work? */
export function HowDRWorksContent() {
  return (
    <div className="flex flex-col gap-[88px] w-full">
      <InfoBlock
        label="Bypassing the Guardian & Policy Engine"
        title="Disaster recovery lets a recovery wallet bypass the MLS Guardian and policy engine, so it can execute transactions even if the Guardian is down."
        body="The recovery wallet can transfer tokens, interact with smart contracts, and authorize account signatures even if the MLS Guardian isn't operational."
      />
      <InfoBlock
        label="Recovery Wallet"
        title="The recovery wallet is an external wallet that your organization controls outside of MLS."
        body={
          <>
            <p>
              The recovery wallet can be any EVM-compatible wallet, including
              hardware and smart contract wallets. Keeping this wallet secure is
              critical.
            </p>
            <p className="mt-3">
              Your organization must have access to the recovery wallet on every
              network where it has MLS accounts.
            </p>
          </>
        }
      />
    </div>
  );
}

/** Page 4: What are the risks of disaster recovery? */
export function RisksContent() {
  return (
    <div className="flex flex-col gap-10 w-full">
      <InfoBlock
        label="Permanent setup"
        title="The recovery wallet can't be changed once setup is complete"
        body="Once disaster recovery has been setup, the recovery wallet cannot be changed or removed."
      />
      <InfoBlock
        label="Stolen recovery wallet"
        title="If the recovery wallet is stolen, an attacker can use it to execute transactions on behalf of your organization"
        body="Because the recovery wallet bypasses the MLS guardian and policy engine, whoever controls it controls your MLS accounts."
      />
      <InfoBlock
        label="Lost recovery wallet"
        title="If you lose access to the recovery wallet, you lose access to disaster recovery"
        body={
          <>
            <p>
              Only the recovery wallet can use disaster recovery. If you
              can&apos;t use the recovery wallet, you can&apos;t use disaster
              recovery.
            </p>
            <p className="mt-3">
              Your organization must have access to the recovery wallet on each
              network where it has MLS accounts deployed.
            </p>
            <p className="mt-3">
              If your organization does not have access to the recovery wallet
              for a network where an MLS account is deployed, it does not have
              access to disaster recovery for accounts on that network.
            </p>
          </>
        }
      />
      <InfoBlock
        label="Recovery wallet security"
        title="Keeping your recovery wallet safe is critical"
        body={
          <>
            <p>
              Keeping your recovery wallet&apos;s credentials out of the hands
              of attackers is critical for preventing loss of funds.
            </p>
            <p className="mt-3">
              It is your organization&apos;s responsibility to store the
              recovery wallet securely.
            </p>
          </>
        }
      />
    </div>
  );
}

/** Page 5: How to keep the recovery wallet safe */
export function KeepSafeContent() {
  return (
    <div className="flex flex-col gap-[88px] w-full">
      <InfoBlock
        label="Secure storage"
        title="Store the recovery wallet using secure offline cold storage."
        body={
          <>
            <p>
              Secure methods of cold storage include paper wallets, offline
              hardware wallets, and multisignature wallets.
            </p>
            <p className="mt-3">
              You may also consider using a trusted third-party disaster
              recovery provider CoinCover.
            </p>
          </>
        }
      />
      <InfoBlock
        label="Limited wallet usage"
        title="Do not use the recovery wallet for anything except for MLS disaster recovery."
        body={
          <>
            <p>
              Using the recovery wallet for anything besides MLS disaster
              recovery risks exposing its credentials.
            </p>
            <p className="mt-3">
              The recovery wallet should only ever be used for MLS disaster
              recovery.
            </p>
          </>
        }
      />
    </div>
  );
}

interface HowToSetupContentProps {
  /** Whether another admin has already created the proposal and is awaiting signatures */
  hasExistingProposal: boolean;
  /** How many admins have already approved (only relevant when hasExistingProposal is true) */
  existingApprovalCount?: number;
  /** Required approvals threshold */
  requiredApprovals: number;
  /** Org admin-operation timelock duration in days for setup initiate → finalize wait */
  adminOperationTimelockDays: number;
  /**
   * Which step the user is currently on.
   *   1 = initiation proposal not yet executed (default, always the case in the setup dialog)
   *   2 = initiation executed, timelock in progress
   *   3 = timelock elapsed, finalization proposal created
   */
  currentStep?: 1 | 2 | 3;
}

// ---------------------------------------------------------------------------
// Timeline primitives
// ---------------------------------------------------------------------------

function TimelineDot({ filled }: { filled: boolean }) {
  return filled ? (
    <div className="w-4 h-4 rounded-full border-2 border-zinc-800 bg-zinc-50 flex items-center justify-center shrink-0">
      <div className="w-[9px] h-[9px] rounded-full bg-zinc-800" />
    </div>
  ) : (
    <div className="w-4 h-4 rounded-full border-2 border-zinc-300 bg-zinc-50 shrink-0" />
  );
}

function TimelineLine({ variant }: { variant: 'gradient' | 'dark' | 'gray' }) {
  const cls =
    variant === 'gradient'
      ? 'bg-gradient-to-b from-zinc-800 to-zinc-300'
      : variant === 'dark'
        ? 'bg-zinc-800'
        : 'bg-zinc-300';
  // Fill the full connector container height so line segments stay continuous
  // between step dots even when step content heights differ.
  return <div className={`w-0.5 h-full rounded ${cls}`} />;
}

/** "Current" pill + right-pointing chevron that floats to the left of a timeline dot */
function CurrentLabel() {
  return (
    <div className="absolute right-[25px] top-[-3px] pointer-events-none">
      {/* Render the tooltip tip with an ::after triangle so it stays flush with the pill. */}
      <div className="relative bg-zinc-700 text-white text-xs font-normal px-[7px] py-[3px] rounded-md whitespace-nowrap after:content-[''] after:absolute after:left-full after:top-1/2 after:-translate-y-1/2 after:w-0 after:h-0 after:border-y-[4px] after:border-y-transparent after:border-l-[5px] after:border-l-zinc-700">
        Current
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 6: How to setup disaster recovery
// ---------------------------------------------------------------------------

/** Page 6: How to setup disaster recovery (two variants based on proposal state) */
export function HowToSetupContent({
  hasExistingProposal,
  existingApprovalCount = 0,
  requiredApprovals,
  adminOperationTimelockDays,
  currentStep = 1,
}: HowToSetupContentProps) {
  const remaining = Math.max(requiredApprovals - existingApprovalCount, 0);
  const firstLineVariant =
    currentStep === 1 ? 'gradient' : currentStep > 1 ? 'dark' : 'gray';
  const secondLineVariant =
    currentStep === 2 ? 'gradient' : currentStep > 2 ? 'dark' : 'gray';

  return (
    <div className="flex flex-col gap-[88px] w-full">
      {/* Step 1 */}
      <div className="flex gap-6 w-full">
        {/* Keep the dot in the same row as "Step 1" so the marker alignment doesn't shift with dynamic body copy. */}
        <div className="relative flex justify-center shrink-0 w-[16px] pt-0.5">
          {currentStep === 1 && <CurrentLabel />}
          <div className="relative z-10">
            <TimelineDot filled={currentStep >= 1} />
          </div>
          <div className="absolute left-1/2 top-4 -translate-x-1/2 h-[calc(100%+88px)] z-0">
            <TimelineLine variant={firstLineVariant} />
          </div>
        </div>
        <div className="flex-1">
          <InfoBlock
            label="Step 1"
            title={
              hasExistingProposal
                ? `Initiate the setup by collecting approvals from ${requiredApprovals} admin${requiredApprovals === 1 ? '' : 's'}`
                : `Start the setup process by collecting approvals from at least ${requiredApprovals} admin${requiredApprovals === 1 ? '' : 's'}`
            }
            body={
              <>
                <p>
                  Starting the setup process requires at least{' '}
                  {requiredApprovals} admin approval
                  {requiredApprovals === 1 ? '' : 's'}.
                </p>
                {hasExistingProposal && (
                  <div className="mt-3 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2.5 flex items-center gap-2">
                    <Info className="w-4 h-4 text-zinc-500 opacity-70 shrink-0" />
                    <p className="text-xs font-medium text-zinc-800">
                      This step has already been approved by{' '}
                      {existingApprovalCount} admin
                      {existingApprovalCount !== 1 ? 's' : ''} and requires{' '}
                      {remaining} more approval{remaining !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}
              </>
            }
          />
        </div>
      </div>

      {/* Step 2 */}
      <div className="flex gap-6 w-full">
        <div className="relative flex justify-center shrink-0 w-[16px] pt-0.5">
          {currentStep === 2 && <CurrentLabel />}
          <div className="relative z-10">
            <TimelineDot filled={currentStep >= 2} />
          </div>
          <div className="absolute left-1/2 top-4 -translate-x-1/2 h-[calc(100%+88px)] z-0">
            <TimelineLine variant={secondLineVariant} />
          </div>
        </div>
        <div className="flex-1">
          <InfoBlock
            label="Step 2"
            title={`Wait at least ${adminOperationTimelockDays} ${adminOperationTimelockDays === 1 ? 'day' : 'days'}`}
            body={
              <>
                <p>
                  Setting up disaster recovery is a timelocked operation with a
                  wait period of {adminOperationTimelockDays}{' '}
                  {adminOperationTimelockDays === 1 ? 'day' : 'days'}.
                </p>
                <p className="mt-3">
                  After starting the setup process, admins must wait for{' '}
                  {adminOperationTimelockDays}{' '}
                  {adminOperationTimelockDays === 1 ? 'day' : 'days'} before
                  they can finalize the setup. This wait period gives admins the
                  opportunity to cancel the setup process if they detect
                  malicious use.
                </p>
              </>
            }
          />
        </div>
      </div>

      {/* Step 3 */}
      <div className="flex gap-6 w-full">
        <div className="relative flex justify-center shrink-0 w-[16px] pt-0.5">
          {currentStep === 3 && <CurrentLabel />}
          <div className="relative z-10">
            <TimelineDot filled={currentStep >= 3} />
          </div>
        </div>
        <div className="flex-1">
          <InfoBlock
            label="Step 3"
            title={`Finalize the setup by collecting approvals from at least ${requiredApprovals} admin${requiredApprovals === 1 ? '' : 's'}`}
            body={`After waiting for the admin-operation timelock (${adminOperationTimelockDays} ${adminOperationTimelockDays === 1 ? 'day' : 'days'}), at least ${requiredApprovals} admin${requiredApprovals === 1 ? '' : 's'} must approve the operation to finalize setup.`}
          />
        </div>
      </div>
    </div>
  );
}

/** Page 7: Start disaster recovery setup — readiness checklist */
export function StartSetupContent() {
  const checklistItems = [
    "Our organization's recovery wallet has been set up",
    "Our organization's recovery wallet is stored securely",
    "I know our organization's recovery wallet's address",
  ];

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-2.5 w-full">
        <p className="font-medium text-zinc-800 text-xl leading-7">
          Ready to start setting up disaster recovery?
        </p>
        <div className="text-sm text-zinc-500 leading-normal">
          <p>
            Make sure your recovery wallet is setup, stored securely, and that
            you have its address readily available.
          </p>
          <p className="mt-3">
            Use the checklist below to make sure you&apos;re ready.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 py-5">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
          Readiness checklist
        </p>
        {checklistItems.map((item) => (
          <div key={item} className="flex items-center gap-2.5">
            <CheckCircle2 className="w-6 h-6 text-zinc-800 shrink-0" />
            <p className="text-base text-zinc-800">{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
